/**
 * useWalletHotspots — Wallet-Detail "Hotspots on this wallet" panel.
 *
 * heliumtracker#45: Die Zuordnung wurde frueher client-seitig rekonstruiert —
 * `GET /api/v2/hotspots` (liefert nur owned AND active) plus ein Detail-Call je
 * Kandidat, um `wallet_address` zu vergleichen. Fuer jede Wallet mit inaktiven
 * (Plan-Kontingent) oder beobachteten Hotspots kam dabei eine leere Liste
 * heraus, waehrend die Kopfzeile den echten `hotspot_count` zeigte (34 vs. 0).
 *
 * Jetzt liefert `GET /api/v2/wallets/:id/hotspots` die Liste serverseitig —
 * eine Query statt N+1, und dieselbe Grundmenge wie `hotspot_count`.
 */
import { useQuery } from '@tanstack/react-query'
import apiClient from '../../lib/api-client'

export type WalletHotspotSummary = {
  id: string
  name: string | null
  hnt24h: number | null
  sparkline: number[]
}

type UseWalletHotspotsResult = {
  hotspots: WalletHotspotSummary[]
  isLoading: boolean
}

export function useWalletHotspots(walletId: number | string, hotspotCount: number): UseWalletHotspotsResult {
  const id = Number(walletId)
  const enabled = hotspotCount > 0 && Number.isFinite(id) && id > 0

  const query = useQuery({
    queryKey: ['wallet', String(id), 'hotspots'],
    queryFn: async (): Promise<WalletHotspotSummary[]> => {
      const result = await apiClient.GET('/api/v2/wallets/{id}/hotspots', {
        params: { path: { id } },
      })
      if (result.error) throw result.error
      const rows = result.data?.data ?? []
      return rows.map((h) => ({
        id: String(h.id),
        name: h.name ?? null,
        hnt24h: h.hnt_24h ?? null,
        sparkline: h.sparkline_data ?? [],
      }))
    },
    enabled,
    staleTime: 60_000,
  })

  return { hotspots: query.data ?? [], isLoading: enabled && query.isLoading }
}
